/**
 * Shared contract for the Inspirations feature (ZPL-127).
 *
 * Single source of truth for the request/response shapes between the API
 * and its clients. File bytes never transit the API: the client uploads
 * directly to Cloudinary via a signed upload payload, then confirms. The
 * allow-lists + size caps below are mirrored client-side for early
 * rejection, but the server is authoritative (it pins the content type
 * into the signature and re-validates the real object at confirm time).
 */
import type { UploadedByDTO } from "./visualizations"

export const INSPIRATION_KINDS = ["image", "pdf", "video", "url"] as const
export type InspirationKind = (typeof INSPIRATION_KINDS)[number]

/** File-backed kinds — everything except `url`. */
export const INSPIRATION_FILE_KINDS = ["image", "pdf", "video"] as const
export type InspirationFileKind = (typeof INSPIRATION_FILE_KINDS)[number]

/**
 * Upload lifecycle. `pending_upload` rows are never surfaced to clients;
 * only `ready` rows are listed. `rejected` is terminal (confirm found the
 * object invalid and deleted it).
 */
export const INSPIRATION_STATUSES = [
	"pending_upload",
	"ready",
	"rejected",
] as const
export type InspirationStatus = (typeof INSPIRATION_STATUSES)[number]

/** Role a stored blob plays for its owning inspiration. */
export const FILE_TYPES = ["original", "thumbnail"] as const
export type FileType = (typeof FILE_TYPES)[number]

/**
 * Allowed upload content types per file kind. `image/svg+xml` and any
 * HTML/XML-ish type are deliberately EXCLUDED — served from the storage
 * origin they execute (stored-XSS). The server pins the chosen type into
 * the V4 signature and re-checks the real bytes (magic-number sniff) at
 * confirm; this list is the client-side pre-filter only.
 */
export const INSPIRATION_ALLOWED_CONTENT_TYPES: Record<
	InspirationFileKind,
	readonly string[]
> = {
	image: ["image/png", "image/jpeg", "image/webp"],
	pdf: ["application/pdf"],
	video: ["video/mp4"],
} as const

/** Per-kind maximum upload size in bytes. Authoritatively enforced at confirm. */
export const INSPIRATION_MAX_BYTES: Record<InspirationFileKind, number> = {
	// Uniform 5 MB cap for v1 across all kinds. Note a plain signed PUT cannot
	// enforce size at ingest — this is the declared limit the confirm step
	// re-checks (stat + delete if over); a signed POST policy content-length-range
	// is the documented v1.1 upgrade for a hard pre-store cap.
	image: 5 * 1024 * 1024,
	pdf: 5 * 1024 * 1024,
	video: 5 * 1024 * 1024,
} as const

export const INSPIRATION_LIMITS = {
	titleMax: 200,
	descriptionMax: 2000,
	/** Max non-deleted inspirations per project (anti-flood quota). */
	perProjectMax: 200,
} as const

/**
 * The file kind an allowed content type belongs to, or `undefined` if the
 * type is not on any allow-list (rejected). Shared so the web upload modal
 * derives `kind` from the picked file exactly as the server does.
 */
export function inspirationKindForContentType(
	contentType: string,
): InspirationFileKind | undefined {
	for (const kind of INSPIRATION_FILE_KINDS) {
		if (INSPIRATION_ALLOWED_CONTENT_TYPES[kind].includes(contentType)) {
			return kind
		}
	}
	return undefined
}

/**
 * An inspiration as returned by the API (JSON-serialized — Dates are ISO
 * strings). `thumbnailUrl` / `sourceUrl` are short-lived signed Cloudinary
 * URLs minted per request (never persisted); thumbnails are on-the-fly
 * transformations (image crop, PDF first page, video poster). `null` where
 * not applicable (e.g. a url-kind's `sourceUrl` is the external `url`).
 */
export interface InspirationDTO {
	id: string
	projectId: string
	kind: InspirationKind
	status: InspirationStatus
	title: string | null
	description: string | null
	/** External link for `url` kind; null otherwise. */
	url: string | null
	/** Signed Cloudinary thumbnail (image crop / PDF page / video poster); null for url until enriched. */
	thumbnailUrl: string | null
	/** Signed URL to the original object (file kinds); null for `url` kind. */
	sourceUrl: string | null
	/** Content type of the original object (file kinds); null for `url`. */
	mimeType: string | null
	createdAt: string
	/** Who uploaded/created this asset (ZPL-265). */
	uploadedBy: UploadedByDTO
	/** Whether the requesting user created this row (drives the delete affordance). */
	canDelete: boolean
}

/** Body for `POST .../projects/:projectId/inspirations` (file kind). */
export interface CreateFileInspirationBody {
	/** Exact MIME of the file to upload; must be on an allow-list. */
	contentType: string
	/** Display-only original filename (sanitized; never used for the object key). */
	filename: string
	/** Client-reported byte size; advisory pre-check (confirm re-stats). */
	declaredSize: number
	title?: string
	description?: string
}

/** A signed Cloudinary direct-upload target — the client POSTs the file here. */
export interface CloudinaryUploadTarget {
	/** Cloudinary upload endpoint for the asset's resource type. */
	uploadUrl: string
	/** Signed multipart form fields to send alongside the file. */
	params: Record<string, string>
}

/** Response for the create-file call — the client uploads to Cloudinary next. */
export interface CreateFileInspirationResponse {
	inspirationId: string
	upload: CloudinaryUploadTarget
	expiresAt: string
}

/** Body for `POST .../projects/:projectId/inspirations/url`. */
export interface CreateUrlInspirationBody {
	url: string
	title?: string
	description?: string
}

/** Body for `PATCH /api/common/inspirations/:id`. At least one field. */
export interface UpdateInspirationBody {
	/** Empty string clears the value (stored as null). */
	title?: string
	/** Empty string clears the value (stored as null). */
	description?: string
}

export interface InspirationResponse {
	inspiration: InspirationDTO
}

export interface InspirationListResponse {
	inspirations: InspirationDTO[]
}
